<script lang="ts">
	import { createPromiseStore } from "../../../../core/utils/createPromiseStore.js";
	import type { ContactFormInput } from "../../../../core/schema/ContactForm.js";
	import sendContactForm from "../../api/mutations/sendContactForm.graphql";
	import LabelledInput from "../Form/LabelledInput.svelte";
	import GdprCheckbox from "../Form/GdprCheckbox.svelte";
	import EmailMarketingCheckbox from "../Form/EmailMarketingCheckbox.svelte";
	import RecaptchaDisclaimer from "../Form/RecaptchaDisclaimer.svelte";
	import Button from "../Button.svelte";
	import Modal from "../Modal.svelte";
	import Form from "../Form/Form.svelte";
	import Message from "../Message.svelte";
	import { mutation } from "../../api/graphql.js";
	import translations from "./ContactForm.translations.json";
	import arrowRight from "../../../../core/assets/icons/arrow-right.svg?raw";
	import { ContactFormType } from "../../../../core/schema/ContactFormType.js";
	import { getTranslate } from "../../utils/getTranslate.js";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import { getTenant } from "../../getTenant.js";
	import { emailRegex } from "../../../../core/utils/emailRegex.js";
	import { phoneRegex } from "../../../../core/utils/phoneRegex.js";

	export let id: string | undefined = undefined;
	export let containerClass = "mx-4 md:mx-16 lg:mx-32";
	const translate = getTranslate(translations);

	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();

	const tenant = getTenant();

	const send = createPromiseStore(async (input: ContactFormInput) => {
		await mutation<boolean>(PUBLIC_GRAPHQL_ENDPOINT, sendContactForm, { input });
	});

	let showSuccessModal = false;
	async function submit(event: CustomEvent<SubmitEvent>): Promise<void> {
		const form = event.detail.target as HTMLFormElement;
		const formData = new FormData(form);
		const emailMarketingApprovedAt = formData.get("emailMarketingApprovedAt") as string;
		const input: ContactFormInput = {
			email: (formData.get("email") as string).trim(),
			...(emailMarketingApprovedAt && { emailMarketingApprovedAt }),
			gdprApprovedAt: formData.get("gdprApprovedAt") as string,
			message: (formData.get("message") as string).trim(),
			name: (formData.get("name") as string).trim(),
			pageUrl: window.location.href,
			phoneNumber: (formData.get("phoneNumber") as string).trim(),
			locale: tenant.locale,
			type: ContactFormType.Contact,
		};
		await send.call(input);
		form.reset();
		showSuccessModal = true;
	}
</script>

<Modal show={showSuccessModal}>
	<p class="h2" slot="title">{translate("thanks")}</p>
	<div class="flex flex-col items-center gap-2">
		<p>{translate("willBeInTouch")}</p>

		<Button
			on:click={() => {
				showSuccessModal = false;
			}}
		>
			{translate("understand")}
		</Button>
	</div>
</Modal>

<div class="border-lttertiary border-y">
	<div class="mx-auto max-w-[120rem]">
		<div class="{containerClass} flex flex-col xl:grid xl:grid-cols-2" id="contact-form">
			<div class="border-lttertiary xl:border-x-none border-x pt-5 max-md:px-2 md:pl-8 md:pt-8 xl:border-l">
				<h2 class="text-ltprimary mx-auto mb-4 text-center uppercase tracking-wider md:text-left lg:mx-[initial]">
					{translate("title")}
				</h2>
				<p class="lg:!text-md/normal mx-auto max-w-2xl text-black lg:mx-[initial]">
					{translate("desc")}
				</p>
			</div>
			<Form class="border-lttertiary border-x" on:submit={submit} {id}>
				<div class="grid w-full items-end">
					<LabelledInput
						class="border-lttertiary border-y lg:border-y-0 lg:border-b [&_.input]:!px-8 [&_.label]:!left-8"
						id="name"
						required
						autocomplete="given-name">{translate("name")}</LabelledInput
					>
					<LabelledInput
						class="border-lttertiary border-b [&_.input]:!px-8 [&_.label]:!left-8"
						id="phoneNumber"
						required
						type="tel"
						autocomplete="tel"
						inputProps={{ pattern: phoneRegex.source }}
					>
						{translate("phone")}
					</LabelledInput>
					<LabelledInput
						class="border-lttertiary border-b [&_.input]:!px-8 [&_.label]:!left-8"
						id="email"
						required
						type="email"
						autocomplete="email"
						inputProps={{ pattern: emailRegex.source }}>{translate("email")}</LabelledInput
					>
				</div>
				<div class="relative w-full">
					<textarea
						id="message"
						name="message"
						class="input-with-label border-lttertiary peer h-64 max-h-[32rem] w-full resize-y border-b px-8 !pb-4 !pt-8 text-xs focus:font-semibold focus:outline-none"
						required
						minlength="10"
					></textarea>
					<label class="label textarea-label !left-8" for="message">{translate("yourMessage")}</label>
				</div>

				<div class="flex flex-col justify-between gap-5 p-2 md:p-4 xl:flex-row xl:items-center xl:p-8">
					<div class="flex min-w-0 flex-col gap-2 xl:flex-1">
						<GdprCheckbox class="justify-start" />
						<EmailMarketingCheckbox class="justify-start" />
					</div>
					<div class="shrink-0">
						<Button
							class="mx-[initial]"
							icon={arrowRight}
							type="submit"
							disabled={$send.status === "loading"}
							variant="primary"
						>
							{#if $send.status === "loading"}
								{translate("submitting")}…
							{:else}
								{translate("submit")}
							{/if}
						</Button>
					</div>
				</div>

				{#if $send.status === "error"}
					<Message variant="error" class="mx-2 mb-4 md:mx-4 xl:mx-8">{translate("somethingWrong")}</Message>
				{/if}

				<RecaptchaDisclaimer class="mx-2 mb-6 md:mx-4 xl:mx-8" />
			</Form>
		</div>
	</div>
</div>
